<template>
  <section v-if="!isReady">
    <ab-loading></ab-loading>
  </section>
  <section v-else-if="record" class="topic">
    <header class="task-title">
      <h2 v-text="record.question"></h2>
    </header>
    <div class="task-text" v-text="record.text"></div>
    <div class="task-action no">
      <yn-action-button
        :key="record.id"
        :key-code="37"
        :disabled="isUpdateRequestPending || isGetNextRequestPending || !!lock"
        @click="update(false)"
      >
        &#10008;
        <span>nah</span>
      </yn-action-button>
    </div>
    <div class="task-action yes">
      <yn-action-button
        :key="record.id"
        :key-code="39"
        :disabled="isUpdateRequestPending || isGetNextRequestPending || !!lock"
        @click="update(true)"
      >
        &#x2714;
        <span>yas</span>
      </yn-action-button>
    </div>
    <footer>
      <div class="task-progress">{{ record.progress.done }} / {{ record.progress.total }}</div>
      <div class="keyboard">
        <div class="keyboard-left">&#x2190;</div>
        <div class="keyboard-top">&#x2191;</div>
        <div class="keyboard-bottom">&#x2193;</div>
        <div class="keyboard-right">&#x2192;</div>
      </div>

      <div>
        <ab-button type="text" color="secondary" title="Back" @click="$router.back()"><i class="home">➜</i></ab-button>
        <ab-button type="text" color="secondary" title="Reload" @click="skip"><i ckass="reload">⟳</i></ab-button>
        <ab-button type="text" color="secondary" title="Report" @click="complaintModalVisible = true"
          ><i class="report">⚑</i></ab-button
        >
      </div>

      <yn-complaint-modal
        v-if="complaintModalVisible"
        @close="complaintModalVisible = false"
        @submit="report"
      ></yn-complaint-modal>
    </footer>
  </section>
  <yn-not-found v-else>
    Nothing for you at this moment. Please, come back later.
  </yn-not-found>
</template>

<script>
import { mapState } from 'vuex';

import Loading from './../common/Loading';
import Button from './../common/Button';
import NotFound from './../NotFound';
import ComplaintModal from './ComplaintModal';
import ActionButton from './../common/ActionButton';

export default {
  components: {
    'ab-loading': Loading,
    'ab-button': Button,
    'yn-not-found': NotFound,
    'yn-complaint-modal': ComplaintModal,
    'yn-action-button': ActionButton
  },
  meta() {
    return {
      title: 'Label Task'
    };
  },
  data() {
    return {
      isReady: false,
      lock: null,
      complaintModalVisible: false
    };
  },
  computed: {
    ...mapState({
      isGetNextRequestPending: s => s.labelling.isGetNextRequestPending,
      isUpdateRequestPending: s => s.labelling.isUpdateRequestPending,
      record: s => s.labelling.record
    })
  },
  async created() {
    await this.$store.dispatch('labelling/next', { id: this.$route.params.id });
    this.isReady = true;
  },
  methods: {
    async update(label) {
      if (this.lock) {
        return;
      }

      this.lock = this.$lock();
      try {
        await this.$store.dispatch('labelling/update', { id: this.record.id, label });
        await this.$store.dispatch('labelling/next', { id: this.$route.params.id });
      } catch (e) {
        this.$toast.error({
          title: 'Label failed',
          message: `Please, try again later or contact our development team.`
        });
      } finally {
        if (this.lock) {
          await this.lock.release();
          this.lock = null;
        }
      }
    },
    async skip() {
      this.lock = this.$lock();
      await this.$store.dispatch('labelling/next', { id: this.$route.params.id });
      this.lock.release();
      this.lock = null;
    },
    async report({ complaint }) {
      this.complaintModalVisible = false;

      this.lock = this.$lock();
      try {
        await this.$store.dispatch('labelling/report', { id: this.record.id, complaint });
        await this.$store.dispatch('labelling/next', { id: this.$route.params.id });
      } catch (e) {
        this.$toast.error({
          title: 'Report failed',
          message: `Please, try again later or contact our development team.`
        });
      } finally {
        if (this.lock) {
          await this.lock.release();
          this.lock = null;
        }
      }
    }
  }
};
</script>

<style scoped lang="scss">
.topic {
  height: 100%;

  display: grid;
  grid-template-rows: 200px minmax(0, 1fr) 200px;
  grid-template-columns: 200px minmax(0, 1fr) 200px;
  grid-gap: 0.5rem;

  h2 {
    font-size: 2rem;
    text-align: center;
  }

  .task-title {
    display: flex;
    align-items: center;
    justify-content: center;
    grid-column: 1/4;
    grid-row: 1/2;
  }

  .task-text {
    // background-color: var(--theme-surface);
    // color: var(--theme-on-surface);
    padding: 2rem 15%;
    font-size: 1.8rem;
    text-align: center;
    grid-column: 2/3;
    grid-row: 2/3;
    overflow-y: auto;
  }

  .task-footer {
    display: grid;
    justify-content: center;
    align-items: center;
    grid-template-columns: 1fr 1fr;
    grid-gap: 2rem;
  }

  .task-action {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
    &.yes {
      grid-column: 3/4;
      grid-row: 1/4;

      button {
        color: var(--theme-success);
        text-transform: uppercase;
        background: radial-gradient(rgba(40, 167, 69, 0.3), rgba(40, 167, 69, 0.1));
      }
    }
    &.no {
      grid-column: 1/2;
      grid-row: 1/4;

      button {
        color: var(--theme-error);
        text-transform: uppercase;
        background: radial-gradient(rgba(220, 53, 69, 0.3), rgba(220, 53, 69, 0.1));
      }
    }

    button {
      width: 170px;
      height: 170px;
    }
    span {
      position: absolute;
      transform: translateY(-10px);
      text-transform: lowercase;
      font-weight: 700;
      font-size: 1rem;
      display: block;
      width: 100%;
      text-align: center;
      font-family: Kanit;
    }
  }

  footer {
    justify-content: flex-end;
    align-items: center;
    display: flex;
    flex-direction: column;
    .task-progress {
      margin-bottom: 5px;
      font-weight: 600;
    }
    i {
      font-size: 1.5rem;
      font-style: normal;
      font-weight: 400;

      &.home {
        transform: rotate(180deg);
      }
    }
  }

  .home-link-wrapper {
    text-align: center;
    font-weight: bold;
  }

  @media only screen and (max-width: 768px) {
    grid-template-rows: 150px minmax(0, 1fr) 150px;
    grid-template-columns: 150px minmax(0, 1fr) 150px;
    height: initial;
    min-height: 100%;

    .task-text {
      grid-column: 1/4;
      grid-row: 2/3;
    }

    .task-action {
      button {
        width: 120px;
        height: 120px;
      }
      &.yes {
        grid-column: 3/4;

        grid-row: 3/4;
      }

      &.no {
        grid-column: 1/2;
        grid-row: 3/4;
      }
    }

    .keyboard {
      display: none;
    }
  }
}

.keyboard {
  display: grid;
  grid-template-columns: 25px 25px 25px;
  grid-template-rows: 25px 25px;
  grid-gap: 1px;
  .keyboard-left,
  .keyboard-top,
  .keyboard-bottom,
  .keyboard-right {
    border: 1px solid #2d313d;
    border-radius: 3px;
    padding: 0 2px;
  }

  .keyboard-left {
    grid-column: 1/2;
    grid-row: 2/3;
    text-align: left;
    background: radial-gradient(rgba(220, 53, 69, 0.3), rgba(220, 53, 69, 0.1));
    color: var(--theme-error);
  }
  .keyboard-top {
    grid-column: 2/3;
    grid-row: 1/2;
    text-align: center;
  }
  .keyboard-bottom {
    grid-column: 2/3;
    grid-row: 2/3;
    text-align: center;
  }
  .keyboard-right {
    grid-column: 3/4;
    grid-row: 2/3;
    text-align: right;
    background: radial-gradient(rgba(40, 167, 69, 0.3), rgba(40, 167, 69, 0.1));
    color: var(--theme-success);
  }
}
</style>
